import Service from '../../../../../services';
import { baseUrlAdmin } from '../../../../../store/apiConstants';
import { AdmissionCategoryChangeUtils } from '../utils';

export class AdmissionCategoryChangeService {
    constructor() {
        this.service = new Service();
    }

    async filterUsersData(lazyParams, globalSearch, dropdownsData, searchLevel, searchType, searchValue) {
        const academicYear = localStorage.getItem('userAcademicYear');
        const url = `${baseUrlAdmin}/user/filter/allusers?academicYear=${academicYear}&limit=${lazyParams.rows}&page=${lazyParams.page + 1}&search=${globalSearch}`;

        const payload = {
            branchId: dropdownsData?.branchIds,
            boardId: dropdownsData?.boardIds,
            classId: dropdownsData?.classIds,
            sectionId: dropdownsData?.sectionId,
            isUserNameSearch: searchLevel === 2 && searchType === 'studentName',
            username: searchValue,
            studentId: dropdownsData?.studentIds,
        };

        try {
            const response = await this.service.post(url, payload, true);
            
            if (response?.status && response?.res?.status) {
                const users = AdmissionCategoryChangeUtils.reFormUsersObj(response.res.Items);
                // Try multiple possible fields for total count from the API response
                const totalRecords = response.res.totalRecords || users.length;
                
                return {
                    success: true,
                    users,
                    totalRecords
                };
            } else {
                return {
                    success: false,
                    error: response.errMessage || 'Failed to fetch users'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: error.message || 'API Error occurred'
            };
        }
    }

    async getAdmissionCategoryOptions() {
        const academicYear = localStorage.getItem('userAcademicYear');
        const url = `${baseUrlAdmin}/admission-types/active?academicYear=${academicYear}`;
        
        try {
            const response = await this.service.get(url, true);
            if (response?.status && response?.res?.data) {
                return {
                    success: true,
                    data: response.res.data
                };
            } else {
                return {
                    success: false,
                    error: 'Failed to get admission category options'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: 'Failed to get admission category options'
            };
        }
    }

    async checkAdmissionChangeEligibility(selectedStudentData, effectiveDate) {
        const payload = {
            user_id: selectedStudentData.user_id,
            academicYear: selectedStudentData.academicYear,
            branchId: selectedStudentData.branchId,
            admissionCategory: selectedStudentData.admissionCategory,
            boardId: selectedStudentData.boardId,
            classId: selectedStudentData.classId,
            sectionId: selectedStudentData.sectionId,
            effectiveDate: effectiveDate
        };

        const url = `${baseUrlAdmin}/statusChange/addmission-change-check`;
        
        try {
            const response = await this.service.post(url, payload, true);
            
            if (response && response.status) {
                return {
                    success: true,
                    data: response?.res?.data?.data || [],
                    message: response?.res?.data?.message || ''
                };
            } else {
                return {
                    success: false,
                    error: response?.errMessage || 'Failed to check admission change eligibility'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: error.message || 'An error occurred while checking admission change eligibility'
            };
        }
    }

    async getAdmissionChangeFeeDetails(selectedStudentData, selectedAdmission, effectiveDate) {
        const payload = {
            user_id: selectedStudentData.user_id,
            academicYear: selectedStudentData.academicYear,
            branchId: selectedStudentData.branchId,
            admissionCategory: selectedAdmission,
            boardId: selectedStudentData.boardId,
            classId: selectedStudentData.classId,
            sectionId: selectedStudentData.sectionId,
            effectiveDate: new Date(effectiveDate),
            admissionCategory: selectedStudentData.admissionCategory,
            newAdmissionCategory: selectedAdmission
        };

        const url = `${baseUrlAdmin}/statusChange/admission-change-fee`;
        
        try {
            const response = await this.service.post(url, payload, true);
            
            if (response && response?.res?.data && response?.res?.data?.status) {
                const totalClosingDueAmt = response?.res?.data?.response?.oldFeeInfo?.feeInfo.reduce(
                    (sum, item) => sum + (item.closingDueAmt || 0), 0
                );
                const totalClosingAdvance = response?.res?.data?.response?.oldFeeInfo?.feeInfo.reduce(
                    (sum, item) => sum + (item.closingAdvance || 0), 0
                );

                return {
                    success: true,
                    hasOldFee: true,
                    oldFeeInfo: response?.res?.data?.response?.oldFeeInfo || [],
                    newFeeResponse: response?.res?.data?.response?.newFeeResponse || {},
                    manualAdjustmentFeeInfo: response?.res?.data?.response?.manualAdjustmentFeeInfo || {},
                    totalClosingDueAmt,
                    totalClosingAdvance,
                    adjustmentRuleNo: response?.res?.data?.response?.adjustmentRuleNo
                };
            } else if (response && response?.res?.data && (!response?.res?.data?.status && response?.res?.data.response)) {
                return {
                    success: true,
                    hasOldFee: false,
                    oldFeeInfo: [],
                    adjustmentRuleNo: response?.res?.data?.response?.adjustmentRuleNo,
                    newFeeResponse: response?.res?.data?.response?.newFeeResponse || {},
                    manualAdjustmentFeeInfo: response?.res?.data?.response?.manualAdjustmentFeeInfo || {},
                    showSuccessMessage: true,
                    successMessage: response?.res?.data?.message || ''
                };
            } else {
                return {
                    success: false,
                    error: response?.res?.data?.message || 'Failed to fetch modified amount'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: error.message || 'An error occurred while fetching modified amount'
            };
        }
    }

    async raiseChangeRequest(payload) {
        try {
            const url = `${baseUrlAdmin}/statusChange/admissionchangerequest/save`;
            const response = await this.service.post(url, payload, true);
            
            if (response?.status && response?.res?.status) {
                return {
                    success: true,
                    message: response.res.message
                };
            } else {
                return {
                    success: false,
                    error: response.errMessage || 'Some error occurred'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Some error occurred'
            };
        }
    }

    async fetchRelatedFeeSchedules(selectedStudentData, newFeeResponse) {
        try {
            const academicYear = selectedStudentData?.academicYear;
            const branchId = selectedStudentData?.branchId;

            if (!academicYear || !branchId) {
                return {
                    success: false,
                    error: 'Academic year or branch ID missing'
                };
            }

            const url = `${baseUrlAdmin}/fee-structure?academicYear=${academicYear}&branchId=${branchId}`;
            const response = await this.service.get(url, true);
            
            if (response?.status && response?.res?.data) {
                const feeStructureIds = newFeeResponse.feeStructureDetails?.map(item => item._id) || [];
                
                // feePlanInfo is an array, so we need to get the first element
                // All fee schedules for a student must use the same planId
                const feePlanInfo = Array.isArray(newFeeResponse.feePlanInfo) 
                    ? newFeeResponse.feePlanInfo[0] 
                    : newFeeResponse.feePlanInfo;
                
                const filteredSchedules = response.res.data?.response.filter(schedule =>
                    schedule.planId === feePlanInfo?._id &&
                    !feeStructureIds.includes(schedule._id)
                );
                
                return {
                    success: true,
                    data: filteredSchedules
                };
            } else {
                return {
                    success: false,
                    error: 'Failed to fetch related fee schedules'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: error.message || 'An error occurred while fetching fee schedules'
            };
        }
    }

    async getFeeStructure(branchId) {
        const url = `${baseUrlAdmin}/fee-category?academicYear=${localStorage.getItem('userAcademicYear')}&branchId=${branchId}`;
        
        try {
            const response = await this.service.get(url, true);
            
            if (response?.status && response?.res?.data?.response) {
                return {
                    success: true,
                    data: response?.res?.data?.response
                };
            } else {
                return {
                    success: false,
                    error: 'An error occurred while Getting Fee Category'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: 'An error occurred while Getting Fee schedule'
            };
        }
    }

    async getInstallmentsList(branchId) {
        const academicYear = localStorage.getItem('userAcademicYear');
        const url = `${baseUrlAdmin}/feeInstallments?academicYear=${academicYear}&branchId=${branchId}`;
        
        try {
            const response = await this.service.get(url, true);
            
            if (response?.res?.status && response.status) {
                return {
                    success: true,
                    data: response?.res?.data
                };
            } else {
                return {
                    success: false,
                    error: response?.message || 'Some error occurred'
                };
            }
        } catch (error) {
            return {
                success: false,
                error: error.message || 'Some error occurred'
            };
        }
    }
}
